import type {
  Account,
  Budget,
  CalendarEvent,
  DailyPlan,
  DailyReview,
  Goal,
  Habit,
  InboxItem,
  Note,
  NoteAttachment,
  Project,
  RecurringRule,
  Task,
  TaskSubtask,
  Transaction,
  WeeklyReview,
} from "../types";
import {
  accountTypeValue,
  clampNumber,
  estimateMinutesValue,
  goalCurrentValue,
  habitTargetPerWeekValue,
  nonNegativeNumberValue,
  numberValue,
  positiveNumberValue,
  progressValue,
} from "./domainValues";

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function sanitizeSubtasks(value: unknown, fallback: TaskSubtask[] = []): TaskSubtask[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value.filter(isRecord).map((item, index) => ({
    id: typeof item.id === "string" && item.id.trim() ? item.id : `subtask_${index}`,
    title: typeof item.title === "string" && item.title.trim() ? item.title.trim() : `Việc con ${index + 1}`,
    done: typeof item.done === "boolean" ? item.done : false,
  }));
}

function sanitizeAttachments(value: unknown, fallback: NoteAttachment[] = []): NoteAttachment[] {
  if (!Array.isArray(value)) {
    return fallback;
  }

  return value
    .filter(isRecord)
    .map((item, index) => {
      const ref: NoteAttachment = {
        id: typeof item.id === "string" && item.id.trim() ? item.id : `attachment_${index}`,
        name: typeof item.name === "string" && item.name.trim() ? item.name.trim() : `Attachment ${index + 1}`,
        size: Math.max(0, Number(item.size) || 0),
        type: typeof item.type === "string" && item.type.trim() ? item.type : "application/octet-stream",
      };
      // Inline base64 (legacy/local) and/or cloud Storage path. Transient `url` not kept.
      if (typeof item.dataUrl === "string" && item.dataUrl) ref.dataUrl = item.dataUrl;
      if (typeof item.path === "string" && item.path) ref.path = item.path;
      if (typeof item.uploadedAt === "string" && item.uploadedAt) ref.uploadedAt = item.uploadedAt;
      return ref;
    })
    .filter((item) => Boolean(item.dataUrl?.startsWith("data:")) || Boolean(item.path));
}

// Every new item gets a createdAt stamp: the sync merge orders collections by createdAt
// (newest first), so without it freshly created items would land at a random position
// after the first sync echo. Caller-provided values win (imports, tests).
function stampCreatedAt(createdAt: string | undefined): string {
  return createdAt ?? new Date().toISOString();
}

export function sanitizeNewTask(task: Omit<Task, "id">): Omit<Task, "id"> {
  return {
    ...task,
    estimateMinutes: estimateMinutesValue(task.estimateMinutes, 30),
    subtasks: sanitizeSubtasks(task.subtasks),
    description: task.description?.trim() || undefined,
    tags: task.tags?.map((t) => t.trim()).filter(Boolean),
    createdAt: stampCreatedAt(task.createdAt),
  };
}

export function sanitizeTaskPatch(patch: Partial<Task>, fallback: Task): Partial<Task> {
  const base: Partial<Task> = {
    ...patch,
    ...("estimateMinutes" in patch ? { estimateMinutes: estimateMinutesValue(patch.estimateMinutes, fallback.estimateMinutes) } : {}),
    ...("subtasks" in patch ? { subtasks: sanitizeSubtasks(patch.subtasks, fallback.subtasks) } : {}),
    ...("description" in patch ? { description: patch.description?.trim() || undefined } : {}),
    ...("tags" in patch ? { tags: Array.isArray(patch.tags) ? patch.tags.map((t) => String(t).trim()).filter(Boolean) : fallback.tags } : {}),
  };
  // D29: auto-manage completedAt when status changes
  if ("status" in patch) {
    if (patch.status === "done" && fallback.status !== "done") {
      base.completedAt = new Date().toISOString();
    } else if (patch.status !== "done" && fallback.status === "done") {
      base.completedAt = undefined;
    }
  }
  return base;
}

export function sanitizeNewProject(project: Omit<Project, "id">): Omit<Project, "id"> {
  return {
    ...project,
    progress: progressValue(project.progress, 0),
    createdAt: stampCreatedAt(project.createdAt),
  };
}

export function sanitizeProjectPatch(patch: Partial<Project>, fallback: Project): Partial<Project> {
  const base: Partial<Project> = "progress" in patch
    ? { ...patch, progress: progressValue(patch.progress, fallback.progress) }
    : { ...patch };
  // D29: auto-manage completedAt when status changes
  if ("status" in patch) {
    if (patch.status === "done" && fallback.status !== "done") {
      base.completedAt = new Date().toISOString();
    } else if (patch.status !== "done" && fallback.status === "done") {
      base.completedAt = undefined;
    }
  }
  return base;
}

export function sanitizeNewGoal(goal: Omit<Goal, "id">): Omit<Goal, "id"> {
  return {
    ...goal,
    current: goalCurrentValue(goal.current, 0),
    target: positiveNumberValue(goal.target, 1),
    objective: goal.objective?.trim() || undefined,
    unit: goal.unit?.trim() || undefined,
    createdAt: stampCreatedAt(goal.createdAt),
  };
}

export function sanitizeGoalPatch(patch: Partial<Goal>, fallback: Goal): Partial<Goal> {
  const base: Partial<Goal> = {
    ...patch,
    ...("current" in patch ? { current: goalCurrentValue(patch.current, fallback.current) } : {}),
    ...("target" in patch ? { target: positiveNumberValue(patch.target, fallback.target) } : {}),
    ...("objective" in patch ? { objective: patch.objective?.trim() || undefined } : {}),
    ...("unit" in patch ? { unit: patch.unit?.trim() || undefined } : {}),
  };
  // D29: auto-manage completedAt when status changes (goal "done" = achieved)
  if ("status" in patch) {
    if (patch.status === "done" && fallback.status !== "done") {
      base.completedAt = new Date().toISOString();
    } else if (patch.status !== "done" && fallback.status === "done") {
      base.completedAt = undefined;
    }
  }
  return base;
}

export function sanitizeNewHabit(habit: Omit<Habit, "id" | "completions">): Omit<Habit, "id" | "completions"> {
  return {
    ...habit,
    targetPerWeek: habitTargetPerWeekValue(habit.targetPerWeek, 5),
    createdAt: stampCreatedAt(habit.createdAt),
  };
}

export function sanitizeHabitPatch(patch: Partial<Habit>, fallback: Habit): Partial<Habit> {
  return "targetPerWeek" in patch
    ? { ...patch, targetPerWeek: habitTargetPerWeekValue(patch.targetPerWeek, fallback.targetPerWeek) }
    : patch;
}

export function sanitizeNewNote(note: Omit<Note, "id" | "updatedAt">): Omit<Note, "id" | "updatedAt"> {
  return {
    ...note,
    title: note.title.trim() || "Untitled note",
    tags: note.tags.map((tag) => tag.trim()).filter(Boolean),
    attachments: sanitizeAttachments(note.attachments),
    createdAt: stampCreatedAt(note.createdAt),
  };
}

export function sanitizeNotePatch(patch: Partial<Note>, fallback: Note): Partial<Note> {
  return {
    ...patch,
    ...("title" in patch ? { title: patch.title?.trim() || fallback.title } : {}),
    ...("tags" in patch ? { tags: Array.isArray(patch.tags) ? patch.tags.map((tag) => String(tag).trim()).filter(Boolean) : fallback.tags } : {}),
    ...("attachments" in patch ? { attachments: sanitizeAttachments(patch.attachments, fallback.attachments) } : {}),
  };
}

export function sanitizeNewTransaction(transaction: Omit<Transaction, "id">): Omit<Transaction, "id"> {
  return {
    ...transaction,
    amount: nonNegativeNumberValue(transaction.amount, 0),
    createdAt: stampCreatedAt(transaction.createdAt),
  };
}

export function sanitizeTransactionPatch(patch: Partial<Transaction>, fallback: Transaction): Partial<Transaction> {
  return "amount" in patch
    ? { ...patch, amount: nonNegativeNumberValue(patch.amount, fallback.amount) }
    : patch;
}

export function sanitizeNewBudget(budget: Omit<Budget, "id">): Omit<Budget, "id"> {
  return {
    ...budget,
    monthlyLimit: positiveNumberValue(budget.monthlyLimit, 1),
    createdAt: stampCreatedAt(budget.createdAt),
  };
}

export function sanitizeBudgetPatch(patch: Partial<Budget>, fallback: Budget): Partial<Budget> {
  return "monthlyLimit" in patch
    ? { ...patch, monthlyLimit: positiveNumberValue(patch.monthlyLimit, fallback.monthlyLimit) }
    : patch;
}

export function sanitizeNewInboxItem(item: Omit<InboxItem, "id">): Omit<InboxItem, "id"> {
  return {
    ...item,
    text: item.text.trim() || "Untitled inbox item",
    hint: item.hint?.trim() || undefined,
    createdAt: stampCreatedAt(item.createdAt),
  };
}

export function sanitizeNewCalendarEvent(event: Omit<CalendarEvent, "id">): Omit<CalendarEvent, "id"> {
  return {
    ...event,
    title: event.title.trim() || "Untitled event",
    createdAt: stampCreatedAt(event.createdAt),
  };
}

export function sanitizeCalendarEventPatch(patch: Partial<CalendarEvent>, fallback: CalendarEvent): Partial<CalendarEvent> {
  return {
    ...patch,
    ...("title" in patch ? { title: patch.title?.trim() || fallback.title } : {}),
  };
}

export function sanitizeNewRecurringRule(rule: Omit<RecurringRule, "id">): Omit<RecurringRule, "id"> {
  return {
    ...rule,
    amount: positiveNumberValue(rule.amount, 1),
    category: rule.category.trim() || "Recurring item",
    dayOfMonth: Math.round(clampNumber(rule.dayOfMonth, 1, 28, 1)),
    note: rule.note?.trim() || undefined,
    createdAt: stampCreatedAt(rule.createdAt),
  };
}

export function sanitizeRecurringRulePatch(patch: Partial<RecurringRule>, fallback: RecurringRule): Partial<RecurringRule> {
  return {
    ...patch,
    ...("amount" in patch ? { amount: positiveNumberValue(patch.amount, fallback.amount) } : {}),
    ...("category" in patch ? { category: patch.category?.trim() || fallback.category } : {}),
    ...("dayOfMonth" in patch ? { dayOfMonth: Math.round(clampNumber(patch.dayOfMonth, 1, 28, fallback.dayOfMonth)) } : {}),
    ...("note" in patch ? { note: patch.note?.trim() || undefined } : {}),
  };
}

export function sanitizeNewAccount(account: Omit<Account, "id">): Omit<Account, "id"> {
  return {
    ...account,
    name: account.name.trim() || "Ví mới",
    type: accountTypeValue(account.type, "cash"),
    openingBalance: numberValue(account.openingBalance, 0), // allow negative (credit cards)
    currency: account.currency?.trim() || "VND",
  };
}

export function sanitizeAccountPatch(patch: Partial<Account>, fallback: Account): Partial<Account> {
  return {
    ...patch,
    ...("name" in patch ? { name: patch.name?.trim() || fallback.name } : {}),
    ...("type" in patch ? { type: accountTypeValue(patch.type, fallback.type) } : {}),
    ...("openingBalance" in patch ? { openingBalance: numberValue(patch.openingBalance, fallback.openingBalance) } : {}),
    ...("currency" in patch ? { currency: patch.currency?.trim() || fallback.currency } : {}),
  };
}

// Daily ritual upserts (id = date). Phase 03 refines MIT ranking/UX; this keeps invariants.
export function sanitizeDailyPlan(plan: DailyPlan): DailyPlan {
  return {
    ...plan,
    id: plan.id || plan.date,
    mitTaskIds: Array.isArray(plan.mitTaskIds) ? plan.mitTaskIds.filter(Boolean).slice(0, 3) : [],
    createdAt: plan.createdAt || new Date().toISOString(),
  };
}

export function sanitizeDailyReview(review: DailyReview): DailyReview {
  const energy =
    typeof review.energy === "number" && Number.isFinite(review.energy)
      ? Math.max(1, Math.min(5, Math.round(review.energy)))
      : undefined;
  const base: DailyReview = {
    ...review,
    id: review.id || review.date,
    doneTaskIds: Array.isArray(review.doneTaskIds) ? review.doneTaskIds.filter(Boolean) : [],
    deferredTaskIds: Array.isArray(review.deferredTaskIds) ? review.deferredTaskIds.filter(Boolean) : [],
    reflection: typeof review.reflection === "string" ? review.reflection : "",
    createdAt: review.createdAt || new Date().toISOString(),
  };
  if (energy === undefined) {
    delete base.energy;
    return base;
  }
  return { ...base, energy };
}

export function sanitizeWeeklyReview(review: WeeklyReview): WeeklyReview {
  const scope: WeeklyReview["scope"] = review.scope === "month" ? "month" : "week";
  const base: WeeklyReview = {
    ...review,
    scope,
    id: review.id || `${scope}_${review.date}`,
    date: typeof review.date === "string" ? review.date : "",
    reflection: typeof review.reflection === "string" ? review.reflection : "",
    createdAt: review.createdAt || new Date().toISOString(),
  };
  const wins = review.wins?.trim();
  const blockers = review.blockers?.trim();
  if (!wins) delete base.wins;
  else base.wins = wins;
  if (!blockers) delete base.blockers;
  else base.blockers = blockers;
  if (typeof review.inboxCleared !== "boolean") delete base.inboxCleared;
  return base;
}
